<script setup>
import { ref, watch, onBeforeUnmount } from 'vue'
import { useEditor, EditorContent } from '@tiptap/vue-3'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import Image from '@tiptap/extension-image'
import { emailSeed, emailHtml, emailReady } from '../email_editor.js'

// The composed body embeds the "How It Works" video button as a linked image;
// without an image node TipTap drops the <img> on load, so it never reaches
// the Outlook draft. Inline so it keeps the link mark inside its paragraph;
// width/height/style preserve the button's fixed email sizing.
const EmailImage = Image.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      width: { default: null },
      height: { default: null },
      style: { default: null },
    }
  },
}).configure({ inline: true })

// WYSIWYG editor for the email BODY. Seeded with the server-composed semantic
// HTML; staff edit the whole letter. The fixed shell + signature are added
// server-side on send (EmailComposer.wrap), so this only edits the body.
const editor = useEditor({
  content: '',
  extensions: [
    StarterKit,
    Link.configure({ openOnClick: false, HTMLAttributes: { rel: null, target: null } }),
    EmailImage,
  ],
  editorProps: {
    attributes: { class: 'email-prose focus:outline-none' },
  },
  onUpdate: ({ editor }) => { emailHtml.value = editor.getHTML() },
})

// Load composed content whenever the vanilla side seeds it.
watch(emailSeed, (seed) => {
  if (!editor.value || !seed) return
  editor.value.commands.setContent(seed.html || '', false)
  emailHtml.value = editor.value.getHTML()
  emailReady.value = !!seed.html
})

const linkUrl = ref('')
function setLink() {
  const url = window.prompt('Link URL', linkUrl.value || 'https://')
  if (url === null) return
  if (url === '') { editor.value.chain().focus().unsetLink().run(); return }
  editor.value.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
}

function is(name, attrs) { return editor.value?.isActive(name, attrs) }

onBeforeUnmount(() => editor.value?.destroy())
</script>

<template>
  <div v-if="editor" class="email-editor">
    <div class="flex flex-wrap gap-1 mb-2">
      <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('bold') }"
        @click="editor.chain().focus().toggleBold().run()"><b>B</b></button>
      <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('italic') }"
        @click="editor.chain().focus().toggleItalic().run()"><i>I</i></button>
      <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('heading', { level: 3 }) }"
        @click="editor.chain().focus().toggleHeading({ level: 3 }).run()">H</button>
      <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('bulletList') }"
        @click="editor.chain().focus().toggleBulletList().run()">• List</button>
      <button type="button" class="btn btn-xs" :class="{ 'btn-active': is('link') }"
        @click="setLink">Link</button>
    </div>
    <!-- Deliberately bg-white in BOTH themes: this pane shows the email as the
         customer receives it, and the quote email is always light-background
         HTML (.email-prose pins its own dark-on-light text colours). -->
    <editor-content :editor="editor"
      class="border border-base-300 rounded-lg bg-white p-4 max-h-[560px] overflow-y-auto" />
  </div>
</template>

<style>
/* Make the editor resemble the sent email (the shell styling is applied on send). */
.email-prose { color: #444444; font-family: Aptos, Calibri, Helvetica, sans-serif; font-size: 14px; line-height: 1.5; }
.email-prose h3 { font-size: 15px; font-weight: bold; color: #09457E; text-decoration: underline; margin: 18px 0 6px; }
.email-prose p { margin: 8px 0; }
.email-prose a { color: #398CE0; text-decoration: underline; }
.email-prose ul { margin: 4px 0; padding-left: 20px; list-style: disc; }
.email-prose .callout { background: #ffff00; }
.email-prose img { display: block; }
</style>
